import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu, Search, Sun, Moon, ShoppingCart, X, LogOut, LayoutDashboard, Languages, ChevronDown, ShieldCheck,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";

const GENDER_GROUPS = [
  { label: "Men", gender: "men", items: [
    { label: "Earrings", category: "earrings" },
    { label: "Chains", category: "necklaces" },
    { label: "Bracelets", category: "bracelets" },
    { label: "Rings", category: "rings" },
  ] },
  { label: "Women", gender: "women", items: [
    { label: "Earrings", category: "earrings" },
    { label: "Necklaces", category: "necklaces" },
    { label: "Bracelets", category: "bracelets" },
    { label: "Rings", category: "rings" },
  ] },
];

const LINKS = [
  { to: "/accessories", label: "Accessories" },
  { to: "/category/watches", label: "Watches" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { t, lang, toggleLang } = useLanguage();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
      setQuery("");
    }
  };

  const go = (to) => { setMenuOpen(false); navigate(to); };

  const menuItemClass = "flex items-center gap-3 w-full py-3 px-2 text-sm uppercase tracking-[0.18em] text-foreground/80 hover:text-gold transition-colors text-left";

  return (
    <header className="sticky top-0 z-50 w-full" data-testid="main-navbar">
      <div className="glass border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="flex items-center justify-between h-[72px]">
            {/* Left: menu only, pushed to the far left */}
            <div className="flex items-center flex-1">
              <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="-ml-2" data-testid="menu-btn" aria-label={t("Menu")}>
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[320px] p-0 flex flex-col">
                  <SheetTitle className="sr-only">{t("Menu")}</SheetTitle>
                  <SheetDescription className="sr-only">{t("Menu")}</SheetDescription>
                  <div className="p-6 border-b border-border">
                    <span className="font-serif font-bold text-2xl tracking-[0.12em] whitespace-nowrap">M&amp;A Jewelry</span>
                    {user && (
                      <div className="mt-2 text-xs text-muted-foreground" data-testid="menu-user-name">
                        {t("Hello")}, {user.name}
                      </div>
                    )}
                  </div>

                  <nav className="flex flex-col p-4 overflow-y-auto flex-1">
                    {GENDER_GROUPS.map((group) => (
                      <div key={group.gender}>
                        <button
                          onClick={() => setOpenGroup((g) => (g === group.gender ? null : group.gender))}
                          className={`${menuItemClass} justify-between`}
                          data-testid={`menu-group-${group.gender}`}
                        >
                          <span>{t(group.label)}</span>
                          <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${openGroup === group.gender ? "rotate-180 text-gold" : ""}`} />
                        </button>
                        {openGroup === group.gender && (
                          <div className="pl-4 pb-2 flex flex-col border-l border-border/60 ml-2">
                            {group.items.map((it) => (
                              <Link
                                key={it.category}
                                to={`/shop?gender=${group.gender}&category=${it.category}`}
                                onClick={() => setMenuOpen(false)}
                                data-testid={`menu-${group.gender}-${it.category}`}
                                className="py-2.5 px-2 text-sm text-foreground/60 hover:text-gold transition-colors"
                              >
                                {t(it.label)}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {LINKS.map((l) => (
                      <Link
                        key={l.to}
                        to={l.to}
                        onClick={() => setMenuOpen(false)}
                        data-testid={`menu-nav-${l.label.toLowerCase()}`}
                        className={menuItemClass}
                      >
                        {t(l.label)}
                      </Link>
                    ))}

                    {user && (
                      <>
                        <div className="my-3 border-t border-border/70" />
                        {user.role === "admin" && (
                          <button onClick={() => go("/admin")} className={menuItemClass} data-testid="menu-admin">
                            <LayoutDashboard className="h-4 w-4" /> {t("Admin Dashboard")}
                          </button>
                        )}
                        <button onClick={() => { logout(); setMenuOpen(false); }} className={menuItemClass} data-testid="menu-logout">
                          <LogOut className="h-4 w-4" /> {t("Sign Out")}
                        </button>
                      </>
                    )}

                    <div className="my-3 border-t border-border/70" />

                    <button onClick={toggleTheme} className={menuItemClass} data-testid="menu-theme-toggle">
                      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                      {theme === "dark" ? t("Light Mode") : t("Dark Mode")}
                    </button>
                    <button onClick={toggleLang} className={menuItemClass} data-testid="menu-lang-toggle">
                      <Languages className="h-4 w-4" /> {lang === "en" ? "Español" : "English"}
                    </button>

                    {!user && (
                      <button
                        onClick={() => go("/admin")}
                        className="mt-2 flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground/70 hover:text-gold transition-colors"
                        data-testid="menu-admin-link"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" /> {t("Admin")}
                      </button>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>

            {/* Center logo */}
            <Link to="/" className="flex-1 text-center" data-testid="logo-link">
              <span className="font-serif font-bold text-2xl sm:text-3xl tracking-[0.12em] whitespace-nowrap">M&amp;A Jewelry</span>
            </Link>

            {/* Right: search + cart */}
            <div className="flex items-center justify-end gap-1 flex-1">
              <button
                onClick={() => setSearchOpen((s) => !s)}
                className="inline-flex items-center justify-center h-10 w-10 text-muted-foreground hover:text-foreground transition-colors"
                data-testid="search-toggle-btn"
                aria-label={t("Search")}
              >
                <Search className="h-5 w-5" />
              </button>
              <Button variant="ghost" size="icon" className="relative -mr-2" onClick={() => navigate("/cart")} data-testid="cart-btn" aria-label={t("Cart")}>
                <ShoppingCart className="h-5 w-5" />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-gold text-gold-foreground text-[10px] h-4 min-w-4 px-1 flex items-center justify-center rounded-full" data-testid="cart-count">
                    {count}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-border glass" data-testid="search-panel">
            <form onSubmit={submitSearch} className="mx-auto max-w-[1400px] px-5 lg:px-10 py-4 flex items-center gap-3">
              <Search className="h-5 w-5 text-muted-foreground" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("Search for rings, necklaces, watches…")}
                className="border-0 focus-visible:ring-0 text-base bg-transparent"
                data-testid="search-input"
              />
              <Button type="submit" size="sm" data-testid="search-submit">{t("Search")}</Button>
              <Button type="button" variant="ghost" size="icon" onClick={() => setSearchOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </form>
          </div>
        )}
      </div>
    </header>
  );
};
